import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  NotificationResponse,
  NotificationPreferenceResponse,
  NotificationPreferenceUpdate,
  UnreadCountResponse,
  PushSubscriptionInput,
  PushSubscriptionDeleteInput,
} from '@sime/shared';

// --- Response Types ---

interface NotificationListResponse {
  success: true;
  data: {
    items: NotificationResponse[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface UnreadCountApiResponse {
  success: true;
  data: UnreadCountResponse;
}

interface NotificationPreferenceApiResponse {
  success: true;
  data: NotificationPreferenceResponse;
}

interface MarkReadResponse {
  success: true;
}

interface PushSubscriptionResponse {
  success: true;
  data: { id: number };
}

// --- Query Keys ---

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (page: number, limit: number) =>
    [...notificationKeys.all, 'list', page, limit] as const,
  unreadCount: () =>
    [...notificationKeys.all, 'unread-count'] as const,
  preferences: () =>
    [...notificationKeys.all, 'preferences'] as const,
};

// --- Query Hooks ---

export function useNotifications(page = 1, limit = 20) {
  return useQuery({
    queryKey: notificationKeys.list(page, limit),
    queryFn: () =>
      apiClient<NotificationListResponse>(
        `/notifications?page=${page}&limit=${limit}`,
      ),
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () =>
      apiClient<UnreadCountApiResponse>('/notifications/unread-count'),
    refetchInterval: 30_000,
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: () =>
      apiClient<NotificationPreferenceApiResponse>('/notifications/preferences'),
  });
}

// --- Mutation Hooks ---

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiClient<MarkReadResponse>(`/notifications/${id}/read`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.all,
      });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient<MarkReadResponse>('/notifications/read-all', {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.all,
      });
    },
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: NotificationPreferenceUpdate) =>
      apiClient<NotificationPreferenceApiResponse>('/notifications/preferences', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.preferences(),
      });
    },
  });
}

export function useRegisterPushSubscription() {
  return useMutation({
    mutationFn: (subscription: PushSubscriptionInput) =>
      apiClient<PushSubscriptionResponse>('/notifications/push-subscription', {
        method: 'POST',
        body: JSON.stringify(subscription),
      }),
  });
}

export function useUnregisterPushSubscription() {
  return useMutation({
    mutationFn: (data: PushSubscriptionDeleteInput) =>
      apiClient<MarkReadResponse>('/notifications/push-subscription', {
        method: 'DELETE',
        body: JSON.stringify(data),
      }),
  });
}

// Re-export types for page components
export type {
  NotificationResponse,
  NotificationPreferenceResponse,
  NotificationPreferenceUpdate,
};
